import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, FileDown, Upload, Loader2, Trash2, Save, Edit3, Calendar as CalendarIcon,
  FileText, Receipt, DollarSign, Eye, AlertCircle, CheckCircle2, X, Pencil,
} from "lucide-react";

function isoToBr(iso) {
  if (!iso) return "—";
  const s = String(iso).split("T")[0];
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}
function money(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return "$ " + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function FechamentoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({ name: "", week_start: "", week_end: "", notes_free_text: "" });
  const [editingNote, setEditingNote] = useState(null); // note object
  const [preview, setPreview] = useState(null); // file url
  const fileInputRef = useRef(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("aab_token") : null;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/fechamentos/${id}`);
      setF(data);
      setHeaderForm({
        name: data.name || "",
        week_start: (data.week_start || "").slice(0, 10),
        week_end: (data.week_end || "").slice(0, 10),
        notes_free_text: data.notes_free_text || "",
      });
    } catch (e) {
      toast.error("Fechamento não encontrado");
      navigate("/fechamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let successCount = 0;
    let ocrFail = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/fechamentos/${id}/notes`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        successCount++;
        if (!data.ocr_success) ocrFail++;
      } catch (e) {
        toast.error(`Erro em ${file.name}: ${e?.response?.data?.detail || "falha"}`);
      }
    }
    setUploading(false);
    if (successCount > 0) {
      toast.success(
        ocrFail === 0
          ? `${successCount} nota(s) processada(s) com sucesso.`
          : `${successCount} nota(s) processada(s). ${ocrFail} precisam de revisão manual.`,
      );
    }
    await load();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDropFiles = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    handleUpload(files);
  };

  const saveHeader = async () => {
    try {
      const { data } = await api.put(`/fechamentos/${id}`, headerForm);
      setF(data);
      setEditingHeader(false);
      toast.success("Fechamento atualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const saveNote = async () => {
    if (!editingNote) return;
    const payload = {
      invoice_date: editingNote.invoice_date || null,
      vin: editingNote.vin ? String(editingNote.vin).toUpperCase().trim() : null,
      year: editingNote.year === "" || editingNote.year == null ? null : Number(editingNote.year),
      make: editingNote.make || null,
      model: editingNote.model || null,
      total_value: editingNote.total_value === "" || editingNote.total_value == null ? null : Number(editingNote.total_value),
    };
    try {
      await api.put(`/fechamentos/${id}/notes/${editingNote.id}`, payload);
      toast.success("Nota atualizada");
      setEditingNote(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar nota");
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Remover esta nota do fechamento?")) return;
    try {
      await api.delete(`/fechamentos/${id}/notes/${noteId}`);
      toast.success("Nota removida");
      await load();
    } catch { toast.error("Erro ao remover"); }
  };

  const pdfUrl = useMemo(() => {
    if (!f) return "";
    const base = process.env.REACT_APP_BACKEND_URL;
    const q = token ? `?auth=${token}` : "";
    return `${base}/api/fechamentos/${f.id}/pdf${q}`;
  }, [f, token]);

  const buildFileSrc = (url) => {
    if (!url) return "";
    const abs = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return token ? `${abs}${abs.includes("?") ? "&" : "?"}auth=${token}` : abs;
  };

  if (loading || !f) {
    return <div className="text-sm text-muted-foreground py-20 text-center">Carregando fechamento...</div>;
  }

  const notes = (f.notes || []).slice().sort((a, b) => {
    const da = a.invoice_date || "9999-99-99";
    const db = b.invoice_date || "9999-99-99";
    return da.localeCompare(db);
  });

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="fechamento-detalhe">
      {/* Back / Header */}
      <div>
        <button
          onClick={() => navigate("/fechamento")}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-3"
          data-testid="back-to-fechamentos"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Todos os Fechamentos
        </button>

        <div className="rounded-xl border border-border bg-card p-5 md:p-6">
          {!editingHeader ? (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <FileText className="w-3 h-3" /> Fechamento Semanal
                </div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight mt-1" data-testid="fech-title">{f.name}</h1>
                <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  <span>{isoToBr(f.week_start)} → {isoToBr(f.week_end)}</span>
                </div>
                {f.notes_free_text && (
                  <div className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{f.notes_free_text}</div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" onClick={() => setEditingHeader(true)} data-testid="edit-header-btn">
                  <Pencil className="w-4 h-4 mr-1.5" /> Editar
                </Button>
                <Button
                  disabled={notes.length === 0}
                  onClick={() => window.open(pdfUrl, "_blank")}
                  data-testid="download-pdf-btn"
                >
                  <FileDown className="w-4 h-4 mr-1.5" /> PDF Consolidado
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Nome / Referência</Label>
                <Input
                  value={headerForm.name}
                  onChange={(e) => setHeaderForm({ ...headerForm, name: e.target.value })}
                  data-testid="edit-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Início</Label>
                  <Input
                    type="date"
                    value={headerForm.week_start}
                    onChange={(e) => setHeaderForm({ ...headerForm, week_start: e.target.value })}
                    data-testid="edit-start-input"
                  />
                </div>
                <div>
                  <Label>Fim</Label>
                  <Input
                    type="date"
                    value={headerForm.week_end}
                    onChange={(e) => setHeaderForm({ ...headerForm, week_end: e.target.value })}
                    data-testid="edit-end-input"
                  />
                </div>
              </div>
              <div>
                <Label>Observações</Label>
                <Textarea
                  rows={3}
                  value={headerForm.notes_free_text}
                  onChange={(e) => setHeaderForm({ ...headerForm, notes_free_text: e.target.value })}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditingHeader(false)}>Cancelar</Button>
                <Button onClick={saveHeader} data-testid="save-header-btn">
                  <Save className="w-4 h-4 mr-1.5" /> Salvar
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Receipt className="w-3 h-3" /> Notas
          </div>
          <div className="text-2xl font-bold mt-1" data-testid="kpi-notes-count">{notes.length}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <DollarSign className="w-3 h-3" /> Valor total
          </div>
          <div className="text-2xl font-bold mt-1" data-testid="kpi-total-value">{money(f.total)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 col-span-2 md:col-span-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" /> Pendências
          </div>
          <div className="text-2xl font-bold mt-1" data-testid="kpi-pending">
            {notes.filter((n) => !n.ocr_success || !n.total_value).length}
          </div>
          <div className="text-[11px] text-muted-foreground">notas com dados incompletos</div>
        </div>
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={onDropFiles}
        className={`rounded-xl border-2 border-dashed p-6 md:p-8 text-center transition ${
          uploading ? "border-primary bg-primary/5" : "border-border bg-card/40 hover:border-primary/60"
        }`}
        data-testid="upload-zone"
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-sm">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <span>Processando notas com IA... isso pode levar alguns segundos.</span>
          </div>
        ) : (
          <div>
            <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <div className="text-base font-medium">Arraste as notas aqui</div>
            <div className="text-xs text-muted-foreground mt-1 mb-3">
              ou clique no botão abaixo · Formatos: JPG, PNG, WEBP ou PDF (máx 15 MB)
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => handleUpload(Array.from(e.target.files || []))}
              data-testid="upload-input"
            />
            <Button onClick={() => fileInputRef.current?.click()} data-testid="upload-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Selecionar Notas
            </Button>
          </div>
        )}
      </div>

      {/* Notes table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="text-sm font-semibold">Notas Unificadas</div>
          <div className="text-xs text-muted-foreground">
            {notes.length} nota{notes.length !== 1 ? "s" : ""} · Total {money(f.total)}
          </div>
        </div>

        {notes.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            Nenhuma nota adicionada ainda. Faça upload de imagens ou PDFs de notas fiscais para começar.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="notes-table">
              <thead className="bg-secondary/40">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-xs uppercase tracking-widest text-muted-foreground w-10">#</th>
                  <th className="px-3 py-2 font-semibold text-xs uppercase tracking-widest text-muted-foreground">Data</th>
                  <th className="px-3 py-2 font-semibold text-xs uppercase tracking-widest text-muted-foreground">Ano</th>
                  <th className="px-3 py-2 font-semibold text-xs uppercase tracking-widest text-muted-foreground">Marca / Modelo</th>
                  <th className="px-3 py-2 font-semibold text-xs uppercase tracking-widest text-muted-foreground">VIN</th>
                  <th className="px-3 py-2 font-semibold text-xs uppercase tracking-widest text-muted-foreground text-right">Valor</th>
                  <th className="px-3 py-2 font-semibold text-xs uppercase tracking-widest text-muted-foreground text-right w-24">Ações</th>
                </tr>
              </thead>
              <tbody>
                {notes.map((n, i) => {
                  const missing = !n.total_value || !n.invoice_date || !n.vin;
                  return (
                    <tr key={n.id} className={`border-t border-border ${missing ? "bg-amber-500/5" : ""}`} data-testid={`note-row-${n.id}`}>
                      <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                      <td className="px-3 py-2">{isoToBr(n.invoice_date)}</td>
                      <td className="px-3 py-2">{n.year || "—"}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">
                          {[n.make, n.model].filter(Boolean).join(" ") || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{n.vin || "—"}</td>
                      <td className="px-3 py-2 text-right font-semibold">{money(n.total_value)}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1 justify-end">
                          {n.source_file_url && (
                            <button
                              className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                              onClick={() => setPreview(n)}
                              title="Ver nota original"
                              data-testid={`view-note-${n.id}`}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                            onClick={() => setEditingNote({ ...n })}
                            title="Editar"
                            data-testid={`edit-note-${n.id}`}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            className="p-1.5 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500"
                            onClick={() => deleteNote(n.id)}
                            title="Remover"
                            data-testid={`del-note-${n.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-foreground bg-secondary/30">
                  <td colSpan={5} className="px-3 py-3 text-right font-bold uppercase text-xs tracking-widest">
                    Total Geral
                  </td>
                  <td className="px-3 py-3 text-right font-bold text-lg" data-testid="total-geral-row">{money(f.total)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Note Dialog */}
      <Dialog open={!!editingNote} onOpenChange={(v) => !v && setEditingNote(null)}>
        <DialogContent data-testid="edit-note-dialog">
          <DialogHeader>
            <DialogTitle>Editar dados da nota</DialogTitle>
            <DialogDescription>
              Ajuste manualmente os campos extraídos pela IA. Deixe em branco os campos que não deseja preencher.
            </DialogDescription>
          </DialogHeader>
          {editingNote && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data da nota</Label>
                  <Input
                    type="date"
                    value={editingNote.invoice_date || ""}
                    onChange={(e) => setEditingNote({ ...editingNote, invoice_date: e.target.value })}
                    data-testid="edit-note-date"
                  />
                </div>
                <div>
                  <Label>Valor total</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingNote.total_value ?? ""}
                    onChange={(e) => setEditingNote({ ...editingNote, total_value: e.target.value })}
                    data-testid="edit-note-total"
                  />
                </div>
              </div>
              <div>
                <Label>VIN</Label>
                <Input
                  value={editingNote.vin || ""}
                  onChange={(e) => setEditingNote({ ...editingNote, vin: e.target.value.toUpperCase() })}
                  maxLength={17}
                  className="font-mono"
                  data-testid="edit-note-vin"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Ano</Label>
                  <Input
                    type="number"
                    value={editingNote.year ?? ""}
                    onChange={(e) => setEditingNote({ ...editingNote, year: e.target.value })}
                    data-testid="edit-note-year"
                  />
                </div>
                <div>
                  <Label>Marca</Label>
                  <Input
                    value={editingNote.make || ""}
                    onChange={(e) => setEditingNote({ ...editingNote, make: e.target.value })}
                    data-testid="edit-note-make"
                  />
                </div>
                <div>
                  <Label>Modelo</Label>
                  <Input
                    value={editingNote.model || ""}
                    onChange={(e) => setEditingNote({ ...editingNote, model: e.target.value })}
                    data-testid="edit-note-model"
                  />
                </div>
              </div>
              {editingNote.raw_hint && (
                <div className="text-[11px] text-muted-foreground border-l-2 border-primary/40 pl-2 italic">
                  Dica da IA: &ldquo;{editingNote.raw_hint}&rdquo;
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNote(null)}>Cancelar</Button>
            <Button onClick={saveNote} data-testid="save-note-btn">
              <Save className="w-4 h-4 mr-1.5" /> Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Note */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Nota original</DialogTitle>
            <DialogDescription className="text-xs">
              {preview?.source_filename || "Arquivo enviado"}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            preview.source_content_type?.startsWith("image/") ? (
              <img
                src={buildFileSrc(preview.source_file_url)}
                alt="Nota"
                className="max-h-[70vh] w-full object-contain rounded"
              />
            ) : (
              <iframe
                title="Nota PDF"
                src={buildFileSrc(preview.source_file_url)}
                className="w-full h-[70vh] rounded border border-border"
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
